import { supabase } from '../lib/supabaseClient';
import { EXPORT_TIERS_STRIPE } from '../lib/stripe';
import { API_URL } from '../config/api';

export interface PaymentIntent {
  id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  client_secret?: string;
}

export interface PaymentResult {
  success: boolean;
  paymentIntent?: PaymentIntent;
  error?: string;
  sessionId?: string;
  amount?: number;
  paymentType?: string;
  checkoutUrl?: string;
}

export interface SubscriptionResult {
  success: boolean;
  subscriptionId?: string;
  error?: string;
  sessionId?: string;
  checkoutUrl?: string;
}

class PaymentService {
  private async getAuthToken(): Promise<string> {
    try {
      console.log('[AUTH-DEBUG] Attempting to get auth token...');
      
      // Check if Clerk is available
      if (!(window as any).Clerk) {
        console.warn('[AUTH-DEBUG] Clerk not available on window object');
        return '';
      }
      
      // Check if session exists
      if (!(window as any).Clerk.session) {
        console.warn('[AUTH-DEBUG] Clerk session not available');
        return '';
      }
      
      // Try to get token with timeout
      // Note: Stripe payments use default JWT, not Supabase template
      const tokenPromise = (window as any).Clerk.session.getToken();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Token request timeout')), 5000)
      );
      
      const token = await Promise.race([tokenPromise, timeoutPromise]) as string;
      
      if (!token) {
        console.warn('[AUTH-DEBUG] No token returned from Clerk');
        return '';
      }
      
      console.log('[AUTH-DEBUG] Successfully retrieved auth token');
      return token;
      
    } catch (error) {
      console.error('[AUTH-DEBUG] Error getting auth token:', error);
      
      // Try alternative method if available
      try {
        if ((window as any).Clerk?.session?.getToken) {
          console.log('[AUTH-DEBUG] Trying alternative token method...');
          // Note: Stripe payments use default JWT, not Supabase template
          const altToken = await (window as any).Clerk.session.getToken();
          if (altToken) {
            console.log('[AUTH-DEBUG] Alternative method succeeded');
            return altToken;
          }
        }
      } catch (altError) {
        console.error('[AUTH-DEBUG] Alternative method also failed:', altError);
      }
      
      return '';
    }
  }

  async createCheckoutSession(
    userId: string,
    priceId: string,
    successUrl: string,
    cancelUrl: string,
    metadata: any = {},
    tier?: string
  ): Promise<PaymentResult> {
    try {
      // Debug logging
      console.log('[PAYMENT-DEBUG] createCheckoutSession called with:', {
        userId,
        priceId,
        tier,
        successUrl,
        cancelUrl,
        metadata
      });
      
      // Store in localStorage for persistence
      localStorage.setItem('debug_checkout_request', JSON.stringify({
        userId,
        priceId,
        tier,
        successUrl,
        cancelUrl,
        metadata
      }));

      // Get auth token
      const token = await this.getAuthToken();
      
      // Prepare headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Add authorization header if token is available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('[PAYMENT-DEBUG] Using auth token for request');
      } else {
        console.warn('[PAYMENT-DEBUG] No auth token available, proceeding without authentication');
      }

      // Create checkout session using backend API with cache-busting timestamp
      const requestBody: any = {
        userId,
        successUrl,
        cancelUrl,
        metadata
      };
      
      // Send tier if provided (preferred), otherwise send priceId
      if (tier) {
        requestBody.tier = tier;
        console.log('[PAYMENT-DEBUG] Sending tier:', tier);
      } else {
        requestBody.priceId = priceId;
        console.log('[PAYMENT-DEBUG] Sending priceId:', priceId);
      }
      
      const response = await fetch(`${API_URL}/api/payment/create-checkout-session?_t=${Date.now()}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });

      console.log('[PAYMENT-DEBUG] API response status:', response.status);
      const session = await response.json();
      console.log('[PAYMENT-DEBUG] API response data:', session);

      // Handle authentication errors specifically
      if (response.status === 401) {
        console.error('[PAYMENT-DEBUG] Authentication failed - token may be missing or invalid');
        throw new Error('Authentication required. Please refresh the page and try again.');
      }

      if (!response.ok) {
        throw new Error(session.error || `Server error: ${response.status}`);
      }

      if (!session.sessionId) {
        throw new Error(session.error || 'Failed to create checkout session');
      }

      return {
        success: true,
        sessionId: session.sessionId,
        checkoutUrl: session.checkoutUrl
      };

    } catch (error) {
      console.error('[PAYMENT-DEBUG] Error creating checkout session:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to create checkout session'
      };
    }
  }

  async redirectToCheckout(sessionId: string, checkoutUrl?: string): Promise<PaymentResult> {
    try {
      console.log('🔍 Starting redirect to checkout with session:', sessionId);
      console.log('🔍 Session ID type:', typeof sessionId);
      console.log('🔍 Session ID length:', sessionId.length);
      console.log('🔍 Session ID starts with:', sessionId.substring(0, 10));
      
      // Use the checkout URL from backend if provided, otherwise construct it
      const redirectUrl = checkoutUrl || `https://checkout.stripe.com/c/pay/${sessionId}`;
      console.log('🔍 Redirecting to:', redirectUrl);
      
      // Validate the URL
      if (!redirectUrl.startsWith('https://checkout.stripe.com/')) {
        throw new Error('Invalid Stripe checkout URL');
      }
      
      // Store the redirect URL for debugging
      localStorage.setItem('debug_redirect_url', redirectUrl);
      localStorage.setItem('debug_redirect_time', new Date().toISOString());
      
      console.log('🔍 About to redirect to:', redirectUrl);
      console.log('🔍 Current window location:', window.location.href);
      
      // Try multiple redirect methods for better compatibility
      try {
        console.log('🔍 Attempting Method 1: Direct location change');
        // Method 1: Direct location change
        window.location.href = redirectUrl;
        console.log('🔍 Method 1 completed - redirect should happen now');
      } catch (redirectError) {
        console.warn('🔍 Direct redirect failed, trying alternative method:', redirectError);
        
        console.log('🔍 Attempting Method 2: Create and click a link');
        // Method 2: Create and click a link
        const link = document.createElement('a');
        link.href = redirectUrl;
        link.target = '_self';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('🔍 Method 2 completed - redirect should happen now');
      }
      
      // Add a small delay to ensure the redirect starts
      console.log('🔍 Waiting 100ms for redirect to start...');
      await new Promise(resolve => setTimeout(resolve, 100));
      console.log('🔍 Delay completed - if you see this, redirect may have failed');
      
      return { success: true };

    } catch (error) {
      console.error('🔍 Error redirecting to checkout:', error);
      return {
        success: false,
        error: 'Failed to redirect to checkout: ' + (error as Error).message
      };
    }
  }

  async processExportPayment(userId: string, amount: number): Promise<PaymentResult> {
    try {
      console.log('🔍 Processing export payment for:', { userId, amount });
      
      // Use the starter tier for one-time exports
      const correctAmount = EXPORT_TIERS_STRIPE.starter.cost;
      console.log('🔍 Using correct amount:', correctAmount, 'instead of passed amount:', amount);
      console.log('🔍 EXPORT_TIERS_STRIPE.starter.cost value:', EXPORT_TIERS_STRIPE.starter.cost);
      console.log('🔍 EXPORT_TIERS_STRIPE.starter object:', EXPORT_TIERS_STRIPE.starter);
      
      const priceId = EXPORT_TIERS_STRIPE.starter.stripePriceId;
      console.log('🔍 Using price ID:', priceId);
      
      const successUrl = `${window.location.origin}/test?payment=success&_t=${Date.now()}`;
      const cancelUrl = `${window.location.origin}/test?payment=cancelled&_t=${Date.now()}`;
      
      console.log('🔍 Creating checkout session with URLs:', { successUrl, cancelUrl });
      
      // Debug: Log the export tiers configuration
      console.log('🔍 EXPORT_TIERS_STRIPE.starter:', EXPORT_TIERS_STRIPE.starter);
      localStorage.setItem('debug_export_tiers', JSON.stringify(EXPORT_TIERS_STRIPE.starter));
      
      const metadata = {
        type: 'export_override',
        amount: correctAmount, // Use the correct amount instead of the parameter
        userId: userId
      };
      
      console.log('🔍 Final metadata being sent:', metadata);
      
      const result = await this.createCheckoutSession(
        userId,
        priceId, // Use the export override price
        successUrl,
        cancelUrl,
        metadata
      );

      console.log('🔍 Checkout session result:', result);

      if (result.success && result.sessionId) {
        console.log('🔍 Redirecting to checkout with session:', result.sessionId);
        console.log('🔍 Checkout URL:', result.checkoutUrl);
        
        // Explicitly handle the redirect
        const redirectResult = await this.redirectToCheckout(result.sessionId, result.checkoutUrl);
        console.log('🔍 Redirect result:', redirectResult);
        
        if (redirectResult.success) {
          console.log('🔍 Redirect successful, should redirect now');
          return { success: true, sessionId: result.sessionId, checkoutUrl: result.checkoutUrl };
        } else {
          console.error('🔍 Redirect failed:', redirectResult.error);
          return { success: false, error: redirectResult.error || 'Failed to redirect to checkout' };
        }
      }

      return result;

    } catch (error) {
      console.error('Error processing export payment:', error);
      return {
        success: false,
        error: 'Failed to process export payment: ' + (error as Error).message
      };
    }
  }

  async processSubscriptionPayment(
    userId: string,
    tier: 'walletCard' | 'starter' | 'pro' | 'business' | 'enterprise',
    amount: number
  ): Promise<SubscriptionResult> {
    try {
      console.log('🔍 Processing subscription payment for:', { userId, tier, amount });
      
      const tierConfig = EXPORT_TIERS_STRIPE[tier];
      if (!tierConfig) {
        throw new Error(`Invalid tier: ${tier}`);
      }
      
      console.log('🔍 Tier config from Stripe:', tierConfig);
      console.log('🔍 Using price ID:', tierConfig.stripePriceId);

      const successUrl = `${window.location.origin}/checkout-success?subscription=success&tier=${tier}`;
      const cancelUrl = `${window.location.origin}/dashboard?subscription=cancelled`;
      
      console.log('🔍 Creating checkout session with URLs:', { successUrl, cancelUrl });
      
      const result = await this.createCheckoutSession(
        userId,
        tierConfig.stripePriceId,
        successUrl,
        cancelUrl,
        {
          type: 'subscription',
          tier: tier,
          amount: amount,
          userId: userId
        },
        tier  // Pass tier for server-side price ID mapping
      );

      console.log('🔍 Checkout session result:', result);

      if (result.success && result.sessionId) {
        return {
          success: true,
          sessionId: result.sessionId,
          checkoutUrl: result.checkoutUrl
        };
      }

      return {
        success: false,
        error: result.error || 'Failed to create subscription'
      };

    } catch (error) {
      console.error('🔍 Error processing subscription payment:', error);
      return {
        success: false,
        error: 'Failed to process subscription payment'
      };
    }
  }

  async createPaymentIntent(
    userId: string, 
    amount: number, 
    currency: string = 'USD',
    paymentType: 'export' | 'subscription' = 'export',
    description?: string
  ): Promise<PaymentResult> {
    try {
      // For immediate payments (like export overrides), we'll use Stripe's payment intent
      const response = await fetch(`${API_URL}/api/payment/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount,
          currency,
          userId,
          paymentType,
          description
        }),
      });

      const data = await response.json();

      if (!data.client_secret) {
        throw new Error('Failed to create payment intent');
      }

      // Log the payment transaction
      const { error } = await supabase
        .from('payment_transactions')
        .insert({
          user_id: userId,
          amount: amount,
          currency: currency,
          payment_type: paymentType,
          description: description,
          stripe_payment_intent_id: data.id,
          status: 'pending'
        });

      if (error) {
        console.error('Error logging payment transaction:', error);
      }

      return {
        success: true,
        paymentIntent: {
          id: data.id,
          amount: data.amount,
          currency: data.currency,
          status: data.status,
          client_secret: data.client_secret
        },
        amount: data.amount,
        paymentType: paymentType
      };

    } catch (error) {
      console.error('Error creating payment intent:', error);
      return {
        success: false,
        error: 'Failed to create payment intent'
      };
    }
  }

  async confirmPayment(
    userId: string,
    paymentIntentId: string,
    paymentMethodId?: string
  ): Promise<PaymentResult> {
    try {
      const response = await fetch(`${API_URL}/api/payment/confirm-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId,
          userId,
          paymentMethodId
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error('Failed to confirm payment');
      }

      // Update the payment transaction status
      const { error } = await supabase
        .from('payment_transactions')
        .update({ status: 'completed' })
        .eq('stripe_payment_intent_id', paymentIntentId);

      if (error) {
        console.error('Error updating payment transaction:', error);
      }

      return {
        success: true,
        paymentIntent: {
          id: paymentIntentId,
          amount: data.amount,
          currency: data.currency,
          status: data.status
        },
        amount: data.amount
      };

    } catch (error) {
      console.error('Error confirming payment:', error);
      return {
        success: false,
        error: 'Failed to confirm payment'
      };
    }
  }

  async getPaymentHistory(userId: string, limit: number = 10): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('payment_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching payment history:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error getting payment history:', error);
      return [];
    }
  }

  async refundPayment(userId: string, paymentIntentId: string): Promise<PaymentResult> {
    try {
      const response = await fetch(`${API_URL}/api/payment/refund-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentIntentId,
          userId
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error('Failed to refund payment');
      }

      // Update the payment transaction status
      const { error } = await supabase
        .from('payment_transactions')
        .update({ status: 'refunded' })
        .eq('stripe_payment_intent_id', paymentIntentId);

      if (error) {
        console.error('Error updating payment transaction:', error);
      }

      return {
        success: true,
        paymentIntent: {
          id: paymentIntentId,
          amount: 0, // Refunded amount is 0
          currency: 'USD',
          status: 'refunded'
        }
      };

    } catch (error) {
      console.error('Error refunding payment:', error);
      return {
        success: false,
        error: 'Failed to refund payment'
      };
    }
  }

  async getSubscriptionStatus(userId: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription status:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getSubscriptionStatus:', error);
      return null;
    }
  }

  async cancelSubscription(userId: string): Promise<PaymentResult> {
    try {
      const token = await this.getAuthToken();

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/payment/cancel-subscription`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ userId }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Subscription cancellation failed');
      }

      return {
        success: true,
        amount: 0,
        paymentType: 'cancel'
      };

    } catch (error) {
      console.error('Error canceling subscription:', error);
      return {
        success: false,
        error: (error as Error).message || 'Failed to cancel subscription'
      };
    }
  }

  async createCustomerPortalSession(userId: string, returnUrl: string): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      // First, get the customer ID for this user
      const customerResponse = await fetch(`/api/customer/${userId}`);
      const customerData = await customerResponse.json();

      if (!customerData.customer) {
        return {
          success: false,
          error: 'No customer found for this user'
        };
      }

      // Create portal session
      const response = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: customerData.customer.id,
          returnUrl
        }),
      });

      const data = await response.json();

      if (data.url) {
        return {
          success: true,
          url: data.url
        };
      } else {
        return {
          success: false,
          error: 'Failed to create portal session'
        };
      }

  } catch (error) {
      console.error('Error creating customer portal session:', error);
      return {
        success: false,
        error: 'Failed to create portal session'
      };
    }
  }

  async handlePaymentSuccess(sessionId: string): Promise<PaymentResult> {
    try {
      console.log('🔍 Handling payment success for session:', sessionId);
      
      // Verify the session with Stripe
      const response = await fetch('/api/verify-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      const sessionData = await response.json();
      
      if (!sessionData.success) {
        throw new Error('Invalid session');
      }

      const session = sessionData.session;
      const userId = session.metadata?.userId;
      const paymentType = session.metadata?.type;
      const amount = session.amount_total / 100; // Convert from cents to dollars

      console.log('🔍 Processing successful payment:', {
        userId,
        paymentType,
        amount,
        sessionId
      });

      // Persist payment data to Supabase
      const { data: paymentData, error: paymentError } = await supabase
        .from('payment_transactions')
        .insert({
          user_id: userId,
          amount: amount,
          currency: session.currency,
          payment_type: paymentType,
          status: 'completed',
          stripe_payment_intent_id: session.payment_intent,
          description: paymentType === 'export_override' ? 'One-time export purchase' : `${session.metadata?.tier} subscription`,
          metadata: session.metadata
        });

      if (paymentError) {
        console.error('❌ Error recording payment transaction:', paymentError);
        throw paymentError;
      }

      console.log('✅ Payment transaction recorded:', paymentData);

      // Handle subscription-specific logic
      if (paymentType === 'subscription' && session.subscription) {
        // Record subscription plan
        const { data: subscriptionData, error: subscriptionError } = await supabase
          .from('subscription_plans')
          .insert({
            user_id: userId,
            plan_type: session.metadata?.tier,
            stripe_subscription_id: session.subscription,
            status: 'active',
            current_period_start: new Date(session.subscription_data?.period_start * 1000),
            current_period_end: new Date(session.subscription_data?.period_end * 1000)
          });

        if (subscriptionError) {
          console.error('❌ Error recording subscription plan:', subscriptionError);
          throw subscriptionError;
        }

        console.log('✅ Subscription plan recorded:', subscriptionData);

        // Update user export stats with new tier
        const { data: statsData, error: statsError } = await supabase
          .from('user_export_stats')
          .upsert({
            user_id: userId,
            tier: session.metadata?.tier,
            subscription_status: 'active',
            subscription_end_date: new Date(session.subscription_data?.period_end * 1000)
          }, { onConflict: 'user_id' });

        if (statsError) {
          console.error('❌ Error updating user export stats:', statsError);
          throw statsError;
        }

        console.log('✅ User export stats updated:', statsData);
      }

      // Handle one-time export logic
      if (paymentType === 'export_override') {
        // Record export log for the one-time purchase
        const { data: exportData, error: exportError } = await supabase
          .from('export_logs')
          .insert({
            user_id: userId,
            payment_amount: amount,
            tier_at_export: 'free', // User is still on free tier
            export_type: 'download'
          });

        if (exportError) {
          console.error('❌ Error recording export log:', exportError);
          throw exportError;
        }

        console.log('✅ Export log recorded:', exportData);
      }

      return {
        success: true,
        sessionId: sessionId,
        amount: amount,
        paymentType: paymentType
      };

    } catch (error) {
      console.error('❌ Error handling payment success:', error);
      return {
        success: false,
        error: 'Failed to process payment success'
      };
    }
  }
}

export const paymentService = new PaymentService(); 